// Image Generation Type Definitions
// Shared types used by ImageGeneratorService and Slot Asset Generator

// === Image Types ===
export type ImageModelId = 'soul-2.0' | 'nano-banana-pro' | 'nano-banana-2' | 'seedream-5.0' | 'flux-kontext' | 'gpt-image-1.5';
export type ImageQualityPreset = '512' | '1K' | '2K' | '4K' | 'custom';
export type ImageStylePreset = 'realistic' | 'cinematic' | 'anime' | 'illustration' | 'pixel-art' | 'coc' | 'immortal-legend' | 'custom';
export type AspectRatio = '1:1' | '4:3' | '3:4' | '16:9' | '9:16' | '2:3' | '3:2' | '21:9' | 'free';
export type SeedMode = 'auto' | 'lock' | 'custom';

export interface ImageModelDef {
  id: ImageModelId;
  name: string;
  tier: 'premium' | 'standard';
  description: string;
  maxResolution: number;
  supportsReferenceImages: boolean;
}

export interface ReferenceImage {
  id: string;
  url: string;
  fileName: string;
  source: 'upload' | 'scene-asset';
}

export interface ImageGenerationSettings {
  prompt: string;
  effectivePrompt?: string;  // Pre-built structured prompt — bypasses buildEffectivePrompt enrichment
  systemInstruction?: string;  // Gemini systemInstruction — separate from user prompt
  negativePrompt?: string;
  model: ImageModelId;
  batchCount: 1 | 2 | 4 | 8;
  aspectRatio: AspectRatio;
  qualityPreset: ImageQualityPreset;
  width: number;
  height: number;
  style: ImageStylePreset;
  customStyleText?: string;
  seedMode: SeedMode;
  seedValue?: number;
  referenceImages: ReferenceImage[];
  styleStrength: number; // 0-1
  removeBackground?: boolean;
}

// === Result Types ===
export type GeneratedResultStatus = 'pending' | 'generating' | 'completed' | 'failed';

export interface GeneratedResult {
  id: string;
  type: 'image' | 'video';
  url: string;
  thumbnailUrl?: string;
  prompt: string;
  model: string;
  width: number;
  height: number;
  style?: string;
  timestamp: number;
  metadata: Record<string, unknown>;
  status?: GeneratedResultStatus;
  filePath?: string;
  error?: string;
}

// === Dimension Logic Types ===
export interface QualityDimensionMap {
  quality: string;
  baseDimension: number;
}

// === API Key Management ===
export type AIProviderKey = 'google' | 'openai' | 'huggingface';

export type AIGeneratorApiKeys = Record<AIProviderKey, string>;

export const DEFAULT_API_KEYS: AIGeneratorApiKeys = {
  google: '',
  openai: '',
  huggingface: '',
};

/** Maps image model IDs to their provider keys (null = free, no key needed) */
export const MODEL_PROVIDER_KEY_MAP: Record<ImageModelId, AIProviderKey | null> = {
  'soul-2.0': 'google',
  'nano-banana-pro': null,
  'nano-banana-2': 'google',
  'seedream-5.0': 'huggingface',
  'flux-kontext': 'huggingface',
  'gpt-image-1.5': 'openai',
};

// === Default Values ===
export const DEFAULT_IMAGE_SETTINGS: ImageGenerationSettings = {
  prompt: '',
  model: 'nano-banana-2',
  batchCount: 1,
  aspectRatio: '1:1',
  qualityPreset: '1K',
  width: 1024,
  height: 1024,
  style: 'realistic',
  customStyleText: '',
  seedMode: 'auto',
  referenceImages: [],
  styleStrength: 0.5,
  removeBackground: false,
};

// Array of all image model IDs for validation
export const ALL_IMAGE_MODEL_IDS: ImageModelId[] = ['soul-2.0', 'nano-banana-pro', 'nano-banana-2', 'seedream-5.0', 'flux-kontext', 'gpt-image-1.5'];
